<template>
  <loader-container :loading="loading">
    <div class="w-full flex flex-col">
      <div class="w-full flex flex-col md:flex-row">
        <div class="w-full md:w-1/4 md:pr-3 mb-0">
          <div class="p-6 border border-grey-light">
            <div class="mb-2">
              <h2 class="text-xl font-semibold mb-5">Your Statistics:</h2>

              <div class="flex items-center justify-between mb-5">
                <h4 class="text-xs md:text-base font-semibold">Commission fee</h4>
                <span class="text-gray-dark">{{ commissionFeePercentage }}%</span>
              </div>

              <range-input
                :min="0"
                :step="0.1"
                :max="5"
                v-model="commissionFeePercentage"
                class="mb-5"
              />

              <div class="flex items-center">
                <span class="text-xs mr-3">Increments of</span>
                <select-input
                  :options="commissionIncrementStepOptions"
                  v-model="commissionIncrementSteps"
                  option-key="name"
                  class-name="mb-2"
                  />
              </div>
            </div>

            <div>
              <div class="flex items-center justify-between mb-3">
                <h4 class="text-xs md:text-base font-semibold">Market share</h4>
                <span class="text-gray-dark">{{ marketSharePercentage }}%</span>
              </div>

              <range-input
                :min="0"
                :step="1"
                :max="100"
                v-model="marketSharePercentage"
                class="mb-5"
              />

              <div class="flex items-center">
                <span class="text-xs mr-3">Increments of</span>
                <select-input
                  :options="marketShareIncrementStepOptions"
                  v-model="marketShareIncrementSteps"
                  option-key="name"
                  class-name="mb-2"
                  />
              </div>
            </div>

          </div>
        </div>

        <div class="w-full md:w-3/4 md:pl-3">
          <div class="relative p-6 border border-grey-light">
            <div class="mb-2 uppercase text-center text-xs text-gray-dark">Commission Fee</div>
            <div class="hidden md:inline uppercase text-center text-xs text-gray-dark rotate-270 absolute" style="left:-15px;">Market Share</div>
            <table-wrapper :scrollX="true" class="md:ml-4">
              <table>
                <tr>
                  <td class="border-none"></td>
                  <td
                    v-for="perc in commissionIncrements"
                    :key="perc"
                    class="text-center font-semibold text-lg"
                  >
                    {{ perc }}%
                  </td>
                </tr>
                <tr v-for="(row, rowI) in figuresTableData" :key="rowI">
                  <td class="text-center bg-white border-none font-semibold text-lg">{{ marketShareIncrements[rowI] }}%</td>
                  <td
                    v-for="(val, cellI) in row"
                    :key="cellI"
                    class="text-center text-base"
                    :class="[
                      selectedRowIndex === rowI && selectedCellIndex === cellI ? 'text-blue-dark font-semibold' : 'text-gray-dark'
                    ]"
                  >
                    <span class="px-4 py-2" :class="[
                      selectedRowIndex === rowI && selectedCellIndex === cellI ? 'bg-blue-lighter rounded' : ''
                    ]">{{ validCell(rowI, cellI) ? val.toLocaleString('en-GB', { style: 'currency', currency:'GBP' }) : '-' }}</span>
                  </td>
                </tr>
              </table>
            </table-wrapper>
          </div>
        </div>
      </div>
    </div>
  </loader-container>
</template>

<script>
import RangeInput from '../../components/forms/RangeInput'
import MathUtils from '@/MathUtils'

export default {
  name: 'IncomeChart',
  components: {
    RangeInput
  },

  data () {
    return {
      loading: false,
      commissionFeePercentage: 1.5,
      commissionIncrementSteps: 0.25,
      commissionIncrementStepOptions: [
        { id: 0.1, name: '0.1%' },
        { id: 0.25, name: '0.25%' },
        { id: 0.5, name: '0.5%' },
        { id: 1, name: '1%' }
      ],
      marketSharePercentage: 5,
      marketShareIncrementSteps: 5,
      marketShareIncrementStepOptions: [
        { id: 1, name: '1%' },
        { id: 5, name: '5%' },
        { id: 10, name: '10%' }
      ],
      selectedRowIndex: 0,
      selectedCellIndex: 1
    }
  },

  props: {
    figures: {
      type: Object,
      default: null
    }
  },

  computed: {
    commissionIncrements () {
      let vals = []
      vals.push(MathUtils.roundTo(
        this.commissionFeePercentage - this.commissionIncrementSteps, 2
      ))

      for (let i = 1; i < 5; i++) {
        vals.push(MathUtils.roundTo(vals[i - 1] + this.commissionIncrementSteps, 2))
      }

      return vals
    },

    marketShareIncrements () {
      let vals = []

      if (this.marketSharePercentage - this.marketShareIncrementSteps > 0) {
        vals.push(MathUtils.roundTo(
          this.marketSharePercentage - this.marketShareIncrementSteps, 1
        ))
      }

      vals.push(MathUtils.roundTo(
        this.marketSharePercentage, 1
      ))

      for (let i = 1; i < 6; i++) {
        vals.push(MathUtils.roundTo(vals[i - 1] + this.marketShareIncrementSteps, 1))
      }

      return vals
    },

    figuresTableData () {
      const commission = this.commissionIncrements
      const marketShare = this.marketShareIncrements
      let vals = []

      for (let i = 0; i < marketShare.length; i++) {
        let row = []
        for (let j = 0; j < commission.length; j++) {
          row.push(
            this.figures.pp_sum * (commission[j] / 100) * (marketShare[i] / 100)
          )
        }
        vals.push(row)
      }

      return vals
    },

    selectedFigure () {
      if (this.selectedRowIndex !== null && this.selectedCellIndex !== null) {
        if (this.validCell(this.selectedRowIndex, this.selectedCellIndex)) {
          return this.figuresTableData[this.selectedRowIndex][this.selectedCellIndex]
        }
      }

      return 0
    },

    avgSoldCommission () {
      return (this.commissionFeePercentage / 100) * this.figures.pp_avg
    }
  },

  methods: {
    validCell (rowI, cellI) {
      return this.commissionIncrements[cellI] >= 0 &&
        (this.marketShareIncrements[rowI] >= 0 && this.marketShareIncrements[rowI] <= 100)
    },

    validNumber (number) {
      return isNaN(number) || !isFinite(number) ? '-' : number
    },

    selectFigure (rowI, cellI) {
      this.selectedRowIndex = rowI
      this.selectedCellIndex = cellI
    },

    roundTo: MathUtils.roundTo
  },

  watch: {
    marketShareIncrements (value) {
      this.selectedRowIndex = value[0] && value[0] === this.marketSharePercentage ? 0 : 1
      this.selectedCellIndex = 1
    }
  }
}
</script>

<style lang="scss" scoped>
.rotate-270 {
  transform: rotate(-90deg);
  top: 50%;
}
td {
  padding: 10px 5px
}
</style>
